import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ArrowLeft } from "lucide-react";

interface Branch {
  id: string;
  name: string;
}

const AddStudent = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<Array<{ id: string; name: string }>>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [userBranchId, setUserBranchId] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [formData, setFormData] = useState({
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    gender: "",
    callingName: "",
    passportNumber: "",
    batchId: "",
    class1Medical: "Pending" as "Done" | "Pending" | "Expired" | "Test Done" | "Retest",
    class2Medical: "Pending" as "Done" | "Pending" | "Expired" | "Test Done" | "Retest",
    dateOfJoining: "",
    notes: "",
    computerNumber: "",
    egcaId: "",
    computerNumberStatus: "Pending" as "Verification Pending" | "Applied" | "Approved" | "Pending",
  });

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }

      // Check if user is super_admin
      const { data: superAdminCheck } = await supabase.rpc('has_role', { 
        _user_id: session.user.id, 
        _role: 'super_admin' 
      });
      setIsSuperAdmin(Boolean(superAdminCheck));

      // Get user's branch from user_roles
      const { data: userRoles } = await supabase
        .from("user_roles")
        .select("branch_id")
        .eq("user_id", session.user.id)
        .not("branch_id", "is", null)
        .limit(1);
      
      if (userRoles && userRoles.length > 0) {
        setUserBranchId(userRoles[0].branch_id);
      }

      // Fetch batches
      const { data: batchData } = await supabase
        .from("batches")
        .select("id, name")
        .order("name");
      
      if (batchData) {
        setBatches(batchData);
        // Filter branches (batches that represent branches)
        const branchList = batchData.filter(b => 
          b.name.startsWith("IC ") || b.name.includes("Calicut") || b.name.includes("Bangalore")
        );
        setBranches(branchList);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate inputs
    if (formData.firstName.trim().length < 2 || formData.firstName.length > 100) {
      toast.error("First name must be between 2 and 100 characters");
      return;
    }
    if (formData.surname.trim() && formData.surname.trim().length > 100) {
      toast.error("Surname must be less than 100 characters");
      return;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (formData.callingName.trim().length < 2 || formData.callingName.length > 200) {
      toast.error("Calling name must be between 2 and 200 characters");
      return;
    }
    if (formData.passportNumber && (formData.passportNumber.trim().length < 5 || formData.passportNumber.length > 20)) {
      toast.error("Passport number/ID must be between 5 and 20 characters");
      return;
    }

    // Super admin must select a branch
    if (isSuperAdmin && !selectedBranchId) {
      toast.error("Please select a branch for the student");
      return;
    }

    setLoading(true);

    // Determine branch_id: super admin uses selected, others use their assigned branch
    const branchIdToUse = isSuperAdmin ? selectedBranchId : userBranchId;

    try {
      const { data, error } = await supabase.from("students").insert({
        first_name: formData.firstName.trim(),
        last_name: formData.surname.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        gender: formData.gender || null,
        calling_name: formData.callingName.trim(),
        office_name: formData.callingName.trim(), // Keep for backward compatibility
        passport_number: formData.passportNumber.trim().toUpperCase() || null,
        batch_id: formData.batchId || null,
        branch_id: branchIdToUse,
        class_1_medical: formData.class1Medical,
        class_2_medical: formData.class2Medical,
        date_of_joining: formData.dateOfJoining,
        notes: formData.notes.trim() || null,
        computer_number: formData.computerNumber.trim() || null,
        egca_id: formData.egcaId.trim() || null,
        computer_number_status: formData.computerNumberStatus,
      }).select().single();

      if (error) throw error;

      // Send email notification if email provided
      if (formData.email && data) {
        try {
          await supabase.functions.invoke('send-student-welcome', {
            body: {
              studentName: `${formData.firstName} ${formData.surname || ''}`.trim(),
              studentEmail: formData.email,
              dateOfJoining: formData.dateOfJoining,
            }
          });
        } catch (emailError) {
          console.error("Failed to send email:", emailError);
          // Don't block the student creation if email fails
        }
      }

      toast.success("Student added successfully!");
      navigate("/students");
    } catch (error: any) {
      toast.error(error.message || "Failed to add student");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout userName={userName}>
      <div className="max-w-3xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Add New Student</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surname">Surname</Label>
                  <Input
                    id="surname"
                    value={formData.surname}
                    onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="student@example.com"
                  />
                  <p className="text-xs text-muted-foreground">Student will receive a welcome email</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Contact number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => setFormData({ ...formData, gender: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="callingName">Calling Name *</Label>
                  <Input
                    id="callingName"
                    value={formData.callingName}
                    onChange={(e) => setFormData({ ...formData, callingName: e.target.value })}
                    required
                    placeholder="Name used for calling/addressing the student"
                  />
                </div>
              </div>

              {/* Branch selection for super admin only */}
              {isSuperAdmin && (
                <div className="space-y-2">
                  <Label htmlFor="branchId">Branch *</Label>
                  <Select
                    value={selectedBranchId}
                    onValueChange={setSelectedBranchId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="passportNumber">Passport Number/ID Number</Label>
                  <Input
                    id="passportNumber"
                    value={formData.passportNumber}
                    onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="batchId">Batch</Label>
                  <Select
                    value={formData.batchId}
                    onValueChange={(value) => setFormData({ ...formData, batchId: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map((batch) => (
                        <SelectItem key={batch.id} value={batch.id}>
                          {batch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="class1Medical">Class 1 Medical Status *</Label>
                  <Select
                    value={formData.class1Medical}
                    onValueChange={(value: "Done" | "Pending" | "Expired" | "Test Done" | "Retest") =>
                      setFormData({ ...formData, class1Medical: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Done">Done</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Expired">Expired</SelectItem>
                      <SelectItem value="Test Done">Test Done</SelectItem>
                      <SelectItem value="Retest">Retest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="class2Medical">Class 2 Medical Status *</Label>
                  <Select
                    value={formData.class2Medical}
                    onValueChange={(value: "Done" | "Pending" | "Expired" | "Test Done" | "Retest") =>
                      setFormData({ ...formData, class2Medical: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Done">Done</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Expired">Expired</SelectItem>
                      <SelectItem value="Test Done">Test Done</SelectItem>
                      <SelectItem value="Retest">Retest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateOfJoining">Date of Joining *</Label>
                <Input
                  id="dateOfJoining"
                  type="date"
                  value={formData.dateOfJoining}
                  onChange={(e) => setFormData({ ...formData, dateOfJoining: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="computerNumber">Computer Number</Label>
                  <Input
                    id="computerNumber"
                    value={formData.computerNumber}
                    onChange={(e) => setFormData({ ...formData, computerNumber: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="egcaId">EGCA ID</Label>
                  <Input
                    id="egcaId"
                    value={formData.egcaId}
                    onChange={(e) => setFormData({ ...formData, egcaId: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="computerNumberStatus">Computer Number Status</Label>
                <Select
                  value={formData.computerNumberStatus}
                  onValueChange={(value: "Verification Pending" | "Applied" | "Approved" | "Pending") =>
                    setFormData({ ...formData, computerNumberStatus: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Verification Pending">Verification Pending</SelectItem>
                    <SelectItem value="Applied">Applied</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes about the student"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button type="submit" disabled={loading} className="flex-1">
                  {loading ? "Adding..." : "Add Student"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AddStudent;